import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BatchStateRequest,
  CheckBatchItem,
  CreateCheckBatchRequest,
  CreateCheckBatchResponse,
  ExportBatchRequest,
  ExportBatchResponse,
  amlbotAPI,
} from '@/api';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  BATCH_EXAMPLE_LIST,
  BatchStatusEnum,
  BatchStatusNames,
  COMMON_CONTAINER_ID,
  navigationPages,
} from '@/config';
import { useApiCall } from '@/hooks';
import { useNavigate } from 'react-router-dom';
import { checkBatchSelectors, userSelectors } from '@/redux/selectors';
import { checkBatchActions } from '@/redux/actions';
import { useAppSelector } from '@/redux/hooks';
import parse from 'html-react-parser';
import {
  CloseOutlined,
  DownloadOutlined,
  PaperClipOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Loader } from '@/components';
import {
  downloadCsv,
  getFormattedDateWithTime,
  getSignalTranslation,
} from '@/helpers';
import { capitalize } from 'lodash';
import { ModalInfo, ModalInfoProps } from '../ModalInfo';

import styles from './BatchModal.module.sass';

interface BatchModalProps extends ModalInfoProps {}

const BatchModal: FC<BatchModalProps> = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOpen, onClose } = props;
  const title = t('translation.batch_title');
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileInput, setFileInput] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const { loading, error, data } = useAppSelector(
    checkBatchSelectors.getCheckBatchSlice,
  );
  const { data: userData } = useAppSelector(userSelectors.getUserSlice);

  useEffect(() => {
    // fetch data on the first entrance
    dispatch(checkBatchActions.fetchBatchHistory({}));
  }, [dispatch]);

  const fileDetach = () => {
    setFileInput(() => null);
    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };

  const onFileAttach = () => {
    if (fileRef.current) {
      fileRef.current.click();
    }
  };

  const handleInputChange = (event: ChangeEvent) => {
    const el = event.target as HTMLInputElement;
    const fileObj = el?.files && el.files[0];

    if (fileObj) {
      setFileInput(() => fileObj);
      setFileName(() => fileObj.name);
    }
  };

  const [makeUpload, uploadLoading, uploadError, uploadErrorCode] = useApiCall(
    async () => {
      if (fileInput) {
        const payload: CreateCheckBatchRequest = {
          list: fileInput,
        };

        const result: CreateCheckBatchResponse =
          await amlbotAPI.createBatch(payload);

        if (result.success) {
          dispatch(checkBatchActions.fetchBatchHistory({}));

          toast.success(t('translation.batch_upload_success'), {
            containerId: COMMON_CONTAINER_ID,
          });
        }
      }

      fileDetach();
    },
  );

  const [makeDownload, downloadLoading, downloadError, downloadErrorCode] =
    useApiCall(async (item: CheckBatchItem) => {
      if (item) {
        const payload: ExportBatchRequest = {
          id: item.id,
        };

        const result: ExportBatchResponse =
          await amlbotAPI.exportBatch(payload);

        if (result.success) {
          const {
            data: { rows },
          } = result;

          if (!rows.length) {
            toast.error('Nothing to export', {
              containerId: COMMON_CONTAINER_ID,
            });
            return;
          }
          const filename = `${item.filename.split('.')[0]}_check_results`;
          const headerRow = rows[0].map((val: string) =>
            capitalize(getSignalTranslation(val)),
          );

          rows[0] = headerRow;
          downloadCsv(rows, filename);
        } else {
          toast.error(downloadError, {
            containerId: COMMON_CONTAINER_ID,
          });
        }
      }
    });

  useEffect(() => {
    if (!uploadLoading && !!uploadError) {
      toast.error(uploadError, { containerId: COMMON_CONTAINER_ID });
    }
  }, [uploadLoading, uploadError]);

  const triggerBatchUpload = () => {
    makeUpload();
    fileDetach();
  };

  const handleDownload = (item: CheckBatchItem) => {
    makeDownload(item);
  };

  const getFileExample = () => {
    downloadCsv(BATCH_EXAMPLE_LIST, 'csv_template.csv');
  };

  const isPending = (status: BatchStatusEnum) => {
    return (
      status === BatchStatusEnum.STATUS_NEW ||
      status === BatchStatusEnum.STATUS_PENDING
    );
  };

  const refreshItem = (itemId: number) => {
    const payload: BatchStateRequest = {
      id: itemId,
    };
    dispatch(checkBatchActions.fetchBatchItem(payload));
  };

  const handleGoToTariffs = () => {
    navigate(navigationPages.TARIFFS.path);
  };

  return (
    <ModalInfo
      isOpen={isOpen}
      title={title}
      className={styles.modal}
      onClose={onClose}
    >
      {!!userData?.promoBalance && (
        <>
          <div className={`${styles.description} ${styles.promo}`}>
            {parse(t('translation.investigation_promo_msg'))}
          </div>
          <Button
            size="m"
            type="primary-gradient"
            corner="rect"
            onClick={handleGoToTariffs}
          >
            <span>{t('translation.promo_tpl_adv_btn')}</span>
          </Button>
        </>
      )}
      {!userData?.promoBalance && (
        <>
          <input
            style={{ display: 'none' }}
            ref={fileRef}
            type="file"
            accept=".csv"
            size={2000000}
            onChange={handleInputChange}
          />
          <div className={styles.description}>
            {parse(t('translation.batch_hint'))}
          </div>
          <div className={styles.attachments}>
            {fileInput && (
              <div className={styles.upload}>
                <CloseOutlined
                  className={styles.iconRemove}
                  onClick={fileDetach}
                />
                {fileInput.name}
                <Button
                  type="primary"
                  corner="rect"
                  size="s"
                  minWidth={100}
                  disabled={false}
                  isLoading={false}
                  onClick={triggerBatchUpload}
                >
                  {t('translation.submit')}
                </Button>
              </div>
            )}
            {!fileInput && (
              <span className={styles.iconButton} onClick={onFileAttach}>
                <PaperClipOutlined />
                {t('translation.batch_attach')}
              </span>
            )}
            <span className={styles.iconButton} onClick={getFileExample}>
              <DownloadOutlined />
              &nbsp;
              {t('translation.batch_example')}
            </span>
          </div>

          {loading && (
            <div className={styles.loader}>
              <Loader />
            </div>
          )}

          {!!data?.items.length && (
            <div className={styles.list}>
              <table>
                <thead>
                  <tr>
                    <th>{t('translation.batch_col_name')}</th>
                    <th>{t('translation.batch_col_status')}</th>
                    <th>{t('translation.batch_col_progress')}</th>
                    <th>{t('translation.batch_col_created')}</th>
                    <th colSpan={2}>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.filename}</td>
                      <td>
                        {t(
                          `translation.batch_status_${
                            BatchStatusNames[item.status]
                          }`,
                        )}
                      </td>
                      <td>{item.progress}%</td>
                      <td>{getFormattedDateWithTime(item.created)}</td>
                      <td>
                        <Button
                          type="bordered"
                          corner="rect"
                          size="s"
                          minWidth={100}
                          disabled={
                            item.status !== BatchStatusEnum.STATUS_COMPLETED
                          }
                          isLoading={false}
                          onClick={() => handleDownload(item)}
                        >
                          {t('translation.batch_download')}
                        </Button>
                      </td>
                      <td>
                        {isPending(item.status) && (
                          <span
                            className={`${styles.iconButton} ${styles.tableAction}`}
                            onClick={() => refreshItem(item.id)}
                          >
                            <ReloadOutlined />
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </ModalInfo>
  );
};

export default BatchModal;
